import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';

import { cadsRoleButtonKeydown, cadsRoleButtonKeyup } from '../../a11y/cads-role-button-keyboard';
import { CadsIconViewBitComponent } from '../../bits/cads-icon-view-bit/cads-icon-view-bit.component';
import type { CadsIconViewBitConfig } from '../../bits/cads-icon-view-bit/cads-icon-view-bit.config';
import { CadsTapViewConfig } from './cads-tap-view.config';

/**
 * **Container only:** a **chromeless** horizontal row—projected content plus optional trailing chevron when tappable.
 * Unlike card view, there is no border, shadow, corner radius, or semantic surface tint; the host passes through layout
 * (`display: contents`) until `tappable` is true, then the root becomes a block with pointer cursor, idle
 * `background-color: var(--cads-color-surface)`, and `:active` feedback with `var(--cads-color-surface-muted)`.
 * **Inner line/body UI is not part of this component.** The inner row applies the same inset padding as
 * {@link CadsCardViewComponent} (`--cads-space-md-sm`) so line components align whether they sit in a tap row or a card.
 * Options are supplied via the `config` input ({@link CadsTapViewConfig}; no i18n inside CADS v1).
 */
@Component({
    selector: 'cads-tap-view',
    imports: [CadsIconViewBitComponent],
    templateUrl: './cads-tap-view.component.html',
    styleUrl: './cads-tap-view.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CadsTapViewComponent {
  /** Tap view options (tappability, trailing icon). Defaults apply when properties are omitted. */
  readonly config = input<CadsTapViewConfig>({});

  /** Fired when the view is tappable and the user activates it (click, Enter on keydown, or Space on keyup). */
  readonly tapped = output<void>();

  /**
   * Resolved config with defaults applied (`tappable` false, `showTrailingIcon` true, `trailingIcon` `chevron_right`,
   * `trailingIconSize` `medium`).
   */
  readonly effectiveConfig = computed(() => {
    const c = this.config();
    return {
      tappable: c.tappable ?? false,
      trailingIcon: c.trailingIcon ?? 'chevron_right',
      showTrailingIcon: c.showTrailingIcon ?? true,
      trailingIconSize: c.trailingIconSize ?? 'medium',
    };
  });

  readonly showTrailingChevron = computed(() => {
    const e = this.effectiveConfig();
    return e.tappable && e.showTrailingIcon && this._isChevronIcon(e.trailingIcon);
  });

  readonly trailingChevronBitConfig = computed((): CadsIconViewBitConfig => {
    const e = this.effectiveConfig();
    return {
      icon: this._trailingGlyphToSymbolId(e.trailingIcon),
      shape: 'icon',
      size: e.trailingIconSize,
      collapsed: true,
      color: 'var(--cads-color-mid-gray)',
    };
  });

  protected _onActivate(): void {
    if (!this.effectiveConfig().tappable) {
      return;
    }
    this.tapped.emit();
  }

  protected _onKeydown(event: KeyboardEvent): void {
    const tappable = this.effectiveConfig().tappable;
    cadsRoleButtonKeydown(event, tappable, () => this._onActivate());
  }

  protected _onKeyup(event: KeyboardEvent): void {
    const tappable = this.effectiveConfig().tappable;
    cadsRoleButtonKeyup(event, tappable, () => this._onActivate());
  }

  private _isChevronIcon(name: string): boolean {
    return name === 'chevron_right' || name === 'chevron-right';
  }

  private _trailingGlyphToSymbolId(name: string): string {
    const n = name.trim();
    if (n === 'chevron_right' || n === 'chevron-right') {
      return 'chevron_right';
    }
    return n.replace(/-/g, '_');
  }
}
